import { plural, t } from '@lingui/core/macro'
import type { Artifact } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { DocChatArtifactEvent } from 'features/knowledgeDocumentViewer/types'

import { ARTIFACT_KINDS } from './kinds'

/** "<Title> drafted", or "<Title> updated · N sections changed" after an edit. */
export function artifactCardTitle(
  event: Pick<
    DocChatArtifactEvent,
    'title' | 'revision' | 'changed_paths' | 'created'
  >
): string {
  const title = event.title
  if (event.created) return t`${title} drafted`
  const changedSections = event.changed_paths.filter(
    (path) => path !== 'title'
  ).length
  if (changedSections === 0) return t`${title} updated`
  return t`${title} updated · ${plural(changedSections, { one: '# section changed', other: '# sections changed' })}`
}

export function openItemsLabel(count: number): string | undefined {
  if (count <= 0) return undefined
  return t`${plural(count, { one: '# open item', other: '# open items' })}`
}

export function artifactSlug(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '')
  return slug.length > 0 ? slug : 'document'
}

/** `<slug>-r<revision>.<extension>`, the extension coming from the kind's download renderer. */
export function artifactFilename(
  artifact: Pick<Artifact, 'kind' | 'title' | 'revision'>
): string {
  const { extension } = ARTIFACT_KINDS[artifact.kind].download
  return `${artifactSlug(artifact.title)}-r${artifact.revision}.${extension}`
}

/** Sections the latest marker changed, only while the artifact is still at that marker's revision. */
export function changedPathsForRevision(
  event: Pick<DocChatArtifactEvent, 'revision' | 'changed_paths'> | undefined,
  revision: number
): string[] {
  if (!event || event.revision !== revision) return []
  return event.changed_paths
}

export function newestDownloadEvent(
  artifact: Artifact
): Artifact['events'][number] | undefined {
  return artifact.events
    .filter((event) => event.type === 'downloaded')
    .reduce<
      Artifact['events'][number] | undefined
    >((newest, event) => (newest === undefined || event.at > newest.at ? event : newest), undefined)
}

/** The newest download, unless it was taken before the current revision. */
export function currentRevisionDownload(
  artifact: Artifact
): Artifact['events'][number] | undefined {
  const newest = newestDownloadEvent(artifact)
  if (!newest || newest.revision < artifact.revision) return undefined
  return newest
}
